import { createReducer, on } from '@ngrx/store';
import { NotificationsDrawerActions } from './notifications-drawer.actions';
import { NOTIFICATION_TYPE_CONFIGS } from '../constants/notifications-drawer.constants';
import { buildInitialTypePageStates, initialState, NotificationsDrawerState } from './notifications-drawer.state';

export const notificationsDrawerReducer = createReducer<NotificationsDrawerState>(
  initialState,

  on(NotificationsDrawerActions.loadNotifications, state => ({
    ...state,
    loading: true,
    error: null
  })),

  on(NotificationsDrawerActions.loadNotificationsSuccess, (state, { response }) => ({
    ...state,
    loading: false,
    categories: response.groupedByCategory,
    totalCount: response.totalCount,
    unreadCount: response.unreadCount,
    typePageStates: buildInitialTypePageStates(response.groupedByCategory)
  })),

  on(NotificationsDrawerActions.loadNotificationsFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error
  })),

  on(NotificationsDrawerActions.loadMoreForType, (state, { typeCode }) => ({
    ...state,
    typePageStates: {
      ...state.typePageStates,
      [typeCode]: {
        ...state.typePageStates[typeCode],
        loadingMore: true
      }
    }
  })),

  on(NotificationsDrawerActions.loadMoreForTypeSuccess, (state, { typeCode, response, appendCount }) => {
    // Only keep appendCount items when refilling (e.g. 1 after a delete);
    // keep all items for a normal user-initiated Show More.
    const newItems = appendCount != null ? response.notifications.slice(0, appendCount) : response.notifications;

    // Compute nextPage from the updated loaded count so the formula is correct
    // regardless of whether this was a full-page load or a single-item refill.
    // Math.ceil(updatedCount / initialLimit) + 1 always points past the last
    // loaded server page for any combination of loads and refills.
    const loadedBefore =
      state.categories.flatMap(cat => cat.notificationsByType).find(nt => nt.notificationTypeCode === typeCode)
        ?.notifications.length ?? 0;
    const updatedCount = loadedBefore + newItems.length;
    const initialLimit = NOTIFICATION_TYPE_CONFIGS[typeCode]?.initialLimit ?? 3;
    const nextPage = Math.ceil(updatedCount / initialLimit) + 1;

    return {
      ...state,
      categories: state.categories.map(cat => ({
        ...cat,
        notificationsByType: cat.notificationsByType.map(nt => {
          if (nt.notificationTypeCode !== typeCode) return nt;
          return {
            ...nt,
            notifications: [...nt.notifications, ...newItems],
            totalCount: response.totalCount
            // unreadCount is NOT overwritten — it reflects the lifetime total
            // for this type and is already maintained by markAsReadSuccess /
            // deleteNotificationSuccess.
          };
        })
      })),
      typePageStates: {
        ...state.typePageStates,
        [typeCode]: {
          nextPage,
          loadingMore: false,
          needsRefill: false
        }
      }
    };
  }),

  on(NotificationsDrawerActions.loadMoreForTypeFailure, (state, { typeCode }) => ({
    ...state,
    typePageStates: {
      ...state.typePageStates,
      [typeCode]: {
        ...state.typePageStates[typeCode],
        loadingMore: false
      }
    }
  })),

  on(NotificationsDrawerActions.markAsReadSuccess, (state, { notificationId }) => ({
    ...state,
    unreadCount: state.unreadCount > 0 ? state.unreadCount - 1 : 0,
    categories: state.categories.map(cat => {
      const hadUnread = cat.notificationsByType.some(nt =>
        nt.notifications.some(n => n.notificationId === notificationId && !n.read)
      );
      return {
        ...cat,
        unreadCount: hadUnread && cat.unreadCount > 0 ? cat.unreadCount - 1 : cat.unreadCount,
        notificationsByType: cat.notificationsByType.map(nt => {
          const notification = nt.notifications.find(n => n.notificationId === notificationId);
          if (!notification || notification.read) return nt;
          return {
            ...nt,
            unreadCount: nt.unreadCount > 0 ? nt.unreadCount - 1 : 0,
            notifications: nt.notifications.map(n => (n.notificationId === notificationId ? { ...n, read: true } : n))
          };
        })
      };
    })
  })),

  on(NotificationsDrawerActions.toggleSectionExpansion, (state, { typeCode }) => ({
    ...state,
    expandedSections: {
      ...state.expandedSections,
      [typeCode]: !state.expandedSections[typeCode]
    }
  })),

  on(NotificationsDrawerActions.collapseSectionExpansion, (state, { typeCode }) => ({
    ...state,
    expandedSections: {
      ...state.expandedSections,
      [typeCode]: false
    }
  })),

  on(NotificationsDrawerActions.resetNotificationsDrawer, state => ({
    ...state,
    expandedSections: {}
  })),

  on(NotificationsDrawerActions.deleteNotificationSuccess, (state, { notificationId, typeCode }) => {
    // Determine if the removed notification was unread before mutating state
    const removedNotification = state.categories
      .flatMap(cat => cat.notificationsByType)
      .find(nt => nt.notificationTypeCode === typeCode)
      ?.notifications.find(n => n.notificationId === notificationId);
    const removedUnread = removedNotification ? !removedNotification.read : false;

    const updatedCategories = state.categories.map(cat => {
      const catHasNotification = cat.notificationsByType.some(
        nt => nt.notificationTypeCode === typeCode && nt.notifications.some(n => n.notificationId === notificationId)
      );
      if (!catHasNotification) return cat;

      return {
        ...cat,
        totalCount: cat.totalCount > 0 ? cat.totalCount - 1 : 0,
        unreadCount: removedUnread && cat.unreadCount > 0 ? cat.unreadCount - 1 : cat.unreadCount,
        notificationsByType: cat.notificationsByType.map(nt => {
          if (nt.notificationTypeCode !== typeCode) return nt;
          return {
            ...nt,
            totalCount: nt.totalCount > 0 ? nt.totalCount - 1 : 0,
            unreadCount: removedUnread && nt.unreadCount > 0 ? nt.unreadCount - 1 : nt.unreadCount,
            notifications: nt.notifications.filter(n => n.notificationId !== notificationId)
          };
        })
      };
    });

    // After removal, check if the type's locally-loaded array is now shorter
    // than the server-side total. If so, mark needsRefill so the effect can
    // fetch the next page and keep the visible list full.
    const updatedTypeEntry = updatedCategories
      .flatMap(cat => cat.notificationsByType)
      .find(nt => nt.notificationTypeCode === typeCode);
    const needsRefill = updatedTypeEntry ? updatedTypeEntry.notifications.length < updatedTypeEntry.totalCount : false;

    return {
      ...state,
      totalCount: state.totalCount > 0 ? state.totalCount - 1 : 0,
      unreadCount: removedUnread && state.unreadCount > 0 ? state.unreadCount - 1 : state.unreadCount,
      categories: updatedCategories,
      typePageStates: {
        ...state.typePageStates,
        [typeCode]: {
          ...state.typePageStates[typeCode],
          needsRefill
        }
      }
    };
  })
);
